// src/screens/TheGap.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 12 Section 5.3 — The Gap
// Full-width red background. Count-up animation. No other content.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { ClientData } from '../types/ClientData';
import { fvLump, fvContributions } from '../utils/planCalculations';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function TheGap({ data }: Props) {
  // Brief 49 S4 — Gap = FreedomNumberInflated − Projected LIQUID pot AT target
  // freedom age (not voyantDoNothing.potAtRetirement which is pot at the
  // do-nothing retirement age and therefore overstates the gap).
  // Methodology matches MilestonesWealth so Gap screen and Wealth Trajectory agree.
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);

  const pGrowthPct = (data.assumptionPensionGrowth || 0.08) * 100;
  const iGrowthPct = (data.assumptionIsaGrowth || 0.08) * 100;
  const cGrowthPct = (data.assumptionCashRate || 0.02) * 100;

  const pensionMonthly =
    (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const isaMonthly = data.planIsaMonthlyContribution || data.isaContribution || 0;

  const startPension = data.pensionBalance || 0;
  const startISA = data.investmentsBalance || 0;
  const startCash =
    (data.efAccount1Balance || 0) +
    (data.efAccount2Balance || 0) +
    (data.savingsAccounts || []).reduce(
      (s: number, a: { balance?: number }) => s + (a.balance || 0),
      0,
    );

  const fvPension =
    fvLump(startPension, pGrowthPct, yearsToFreedom) +
    fvContributions(pensionMonthly, pGrowthPct, yearsToFreedom);
  const fvISA =
    fvLump(startISA, iGrowthPct, yearsToFreedom) +
    fvContributions(isaMonthly, iGrowthPct, yearsToFreedom);
  const fvCash = fvLump(startCash, cGrowthPct, yearsToFreedom);

  const projectedLiquidPotAtFreedomAge = fvPension + fvISA + fvCash;

  const gap = Math.max(
    0,
    (data.freedomNumberInflated || 0) - projectedLiquidPotAtFreedomAge,
  );
  const doNothingAge = data.voyantDoNothing.retirementAge;

  const [displayValue, setDisplayValue] = useState(0);
  const [animationDone, setAnimationDone] = useState(false);
  const animRef = useRef<number>(0);

  // Count-up animation: 1.5 seconds, ~60 steps
  useEffect(() => {
    if (gap <= 0) {
      setDisplayValue(0);
      setAnimationDone(true);
      return;
    }

    setAnimationDone(false);
    setDisplayValue(0);

    const duration = 1500; // ms
    const steps = 60;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      // Ease out cubic
      const t = currentStep / steps;
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayValue(Math.round(eased * gap));

      if (currentStep >= steps) {
        clearInterval(interval);
        setDisplayValue(gap);
        setAnimationDone(true);
      }
    }, stepDuration);

    animRef.current = interval as unknown as number;
    return () => clearInterval(interval);
  }, [gap]);

  return (
    <div className="p-6 flex items-center justify-center min-h-[500px]">
      <div className="bg-red-600 rounded-2xl p-12 w-full max-w-3xl text-center text-white">
        <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-white/60 mb-8">
          THE GAP
        </h2>

        <div className="text-6xl md:text-7xl font-bold mb-8 tabular-nums">
          £{displayValue.toLocaleString()}
        </div>

        <p className="text-white/80 text-lg mb-8 leading-relaxed">
          Between where you're heading and the retirement you want.
        </p>

        {doNothingAge && (
          <p className="text-white/60 text-sm mb-10">
            Do-nothing retirement age: {doNothingAge}
          </p>
        )}

        <div className="border-t border-white/30 pt-8">
          <p
            className="text-white/70 text-sm italic transition-opacity duration-700"
            style={{ opacity: animationDone ? 1 : 0 }}
          >
            This is what we're solving today.
          </p>
        </div>
      </div>
    </div>
  );
}
